/* eslint-disable */
import { get } from "lodash";
import { bootstrapHTML } from "../../shared/bootstrapHTML";
import { formatDateOnly, formatDollar, formatPercentage } from "../../shared/utility";

const parseJson = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch (e) {
            return fallback;
        }
    }
    return value;
};

const asArray = (value) => Array.isArray(value) ? value : [];

const escapeHtml = (value) => String(value === null || value === undefined ? "" : value)
    .split("&").join("&amp;")
    .split("<").join("&lt;")
    .split(">").join("&gt;")
    .split('"').join("&quot;");

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return value;
    const parsed = parseFloat(String(value).split("$").join("").split(",").join("").split("%").join(""));
    return Number.isNaN(parsed) ? 0 : parsed;
};

const money = (value) => formatDollar(toNumber(value));

const percent = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return formatPercentage(toNumber(value));
};

const nameFromContact = (contact = {}) => {
    const company = get(contact, "company_name", "");
    if (company) return company;
    return [get(contact, "name", get(contact, "first_name", "")), get(contact, "last_name", "")].filter(Boolean).join(" ");
};

const renderPartyNames = (rsData, field) => asArray(get(rsData, field, []))
    .map(nameFromContact)
    .filter(Boolean)
    .join(", ");

const lineName = (line) => escapeHtml(line.name || "");

const normalizeBasisLine = (line) => ({
    name: line.name || "",
    percentage: line.percentage || "",
    dollar_value: line.dollar_value || "0.00",
    applies_to: line.applies_to || "",
    side: line.side || ""
});

const groupLedgerRows = (ledgerRows, side) => {
    const grouped = {};
    asArray(ledgerRows)
        .filter(row => String(row.side || "").toLowerCase() === side)
        .forEach(row => {
            const key = `${row.associate_id || ""}`;
            if (!grouped[key]) {
                grouped[key] = {
                    name: row.associate_name || `Associate ${row.associate_id}`,
                    percentage: toNumber(row.associate_percentage) * 100,
                    dollar_value: 0,
                    due_to_associate_number: 0,
                    house_split_number: 0,
                    level_numbers: []
                };
            }
            grouped[key].dollar_value += toNumber(row.assigned_gci_amount);
            grouped[key].due_to_associate_number += toNumber(row.associate_payout_amount);
            grouped[key].house_split_number += toNumber(row.house_amount);
            if (row.level_number && grouped[key].level_numbers.indexOf(row.level_number) === -1) {
                grouped[key].level_numbers.push(row.level_number);
            }
        });

    return Object.keys(grouped).map(key => {
        const row = grouped[key];
        const associateSplit = row.dollar_value > 0 ? (row.due_to_associate_number / row.dollar_value) * 100 : 0;
        return {
            name: row.name,
            percentage: row.percentage,
            dollar_value: row.dollar_value,
            associate_split_percent: associateSplit,
            house_split_percent: 100 - associateSplit,
            due_to_associate_number: row.due_to_associate_number,
            house_split_number: row.house_split_number,
            gci_level: row.level_numbers.join(", "),
            is_override: false
        };
    });
};

const buildLegacyDistObj = ({ basis, ledgerRows }) => {
    const lesseeAssociates = groupLedgerRows(ledgerRows, "lessee");
    const lessorAssociates = groupLedgerRows(ledgerRows, "lessor");
    const allAssocs = {};

    lesseeAssociates.concat(lessorAssociates).forEach(row => {
        allAssocs[row.name] = (allAssocs[row.name] || 0) + toNumber(row.due_to_associate_number);
    });

    const allLedgerRows = asArray(ledgerRows);
    const totalHouse = allLedgerRows.reduce((sum, row) => sum + toNumber(row.house_amount), 0);
    const totalAssociates = allLedgerRows.reduce((sum, row) => sum + toNumber(row.associate_payout_amount), 0);

    return {
        presplits: asArray(get(basis, "presplits", [])).map(line => ({
            name: line.name || "",
            perc: line.percentage || "",
            val: line.dollar_value || "0.00",
            applies_to: line.applies_to || ""
        })),
        lessee_outside_brokers: asArray(get(basis, "lessee_outside_brokers", [])).map(normalizeBasisLine),
        lessor_outside_brokers: asArray(get(basis, "lessor_outside_brokers", [])).map(normalizeBasisLine),
        lessee_referrals: asArray(get(basis, "lessee_referrals", [])).map(normalizeBasisLine),
        lessor_referrals: asArray(get(basis, "lessor_referrals", [])).map(normalizeBasisLine),
        lessee_illi_associates: lesseeAssociates,
        lessor_illi_associates: lessorAssociates,
        total_outside_brokers: get(basis, "totals.outside_brokers", "0.00"),
        total_referrals: get(basis, "totals.referrals", "0.00"),
        total_illi_associates: totalAssociates,
        total_house: totalHouse,
        total_paid: get(basis, "payment_amount", "0.00"),
        all_assocs: allAssocs
    };
};

const renderEmptySix = () => `
    <tr>
        <td><em>None</em></td>
        <td class="center"></td>
        <td class="right"></td>
        <td class="center"></td>
        <td class="center"></td>
        <td class="right"></td>
    </tr>
`;

const renderDeductionRows = (items) => {
    if (items.length === 0) return renderEmptySix();
    return items.map(item => `
        <tr>
            <td>${lineName(item)}</td>
            <td class="center">${percent(item.percentage)}</td>
            <td class="right">${money(item.dollar_value)}</td>
            <td class="center">-</td>
            <td class="center">-</td>
            <td class="right">-</td>
        </tr>
    `).join("");
};

const renderAssociateRows = (items) => {
    if (items.length === 0) return renderEmptySix();
    return items.map(item => `
        <tr>
            <td>${lineName(item)}</td>
            <td class="center">${percent(item.percentage)}</td>
            <td class="right">${money(item.dollar_value)}</td>
            <td class="center">${percent(item.associate_split_percent)}</td>
            <td class="center">${percent(item.house_split_percent)}</td>
            <td class="right">${money(item.due_to_associate_number)}</td>
        </tr>
    `).join("");
};

const renderSideTable = ({ title, outsideBrokers, referrals, associates }) => `
    <h3 style="margin:10px 0 6px; font-size:10px; letter-spacing:.12em; color:#444; text-transform:uppercase;">${escapeHtml(title)}</h3>
    <table aria-label="${escapeHtml(title)}">
      <thead>
        <tr>
          <th> </th>
          <th class="center">Percentage</th>
          <th class="right">Dollar Value</th>
          <th class="center">Associate Split</th>
          <th class="center">House Split</th>
          <th class="right">Due to Associate</th>
        </tr>
      </thead>
      <tbody>
        <tr class="group"><td colspan="6">Outside Brokers</td></tr>
        ${renderDeductionRows(outsideBrokers)}
        <tr class="group"><td colspan="6">Referrals</td></tr>
        ${renderDeductionRows(referrals)}
        <tr class="group"><td colspan="6">illi Associates</td></tr>
        ${renderAssociateRows(associates)}
      </tbody>
    </table>
`;

const headerHTML = `
<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>RS - Payment Report</title>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
${bootstrapHTML}
:root{
  --ink:#231f20;
  --muted:#6b6b6b;
  --line:#e6e6e6;
  --light:#f4f5f7;
  --brand:#e21e2e;
  --brand-dark:#c21927;
}
*{box-sizing:border-box}
html,body{margin:0;color:var(--ink);font-family:Inter,system-ui,Arial,sans-serif}
.page{margin:32px auto;background:#fff}
.header{display:grid;grid-template-columns:160px 1fr;gap:24px;align-items:center;margin-bottom:18px}
.logo{width:40px;height:auto}
.title{font-weight:600;letter-spacing:.18em;font-size:22px}
.kvlist{display:grid;grid-template-columns:150px 1fr;column-gap:14px;row-gap:3px;margin:0;font-size:10px;font-variant-numeric:tabular-nums}
.kvlist dt{margin:0;color:#666;font-weight:500;white-space:nowrap}
.kvlist dt::after{content:":";padding-left:6px}
.kvlist dd{margin:0;font-weight:500;color:#222;overflow-wrap:anywhere}
.bar{margin:10px 0 8px;background:#f0f0f0;color:#3a3a3a;font-weight:700;text-align:center;padding:5px 12px;letter-spacing:.12em;font-size:10px}
.bar--brand{background:var(--brand);color:#fff;margin-top:10px}
table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:16px}
th,td{padding:4px 10px;vertical-align:top}
thead th{font-weight:700;color:#3a3a3a;border-bottom:2px solid var(--line);background:#fafafa;text-transform:uppercase;font-size:10px;letter-spacing:.06em}
tbody tr{border-bottom:1px solid var(--line)}
.group{color:var(--muted);font-weight:600;background:#fff}
.right{text-align:right}
.center{text-align:center}
.totals-bar{background:#fff;border-top:2px solid var(--line);border-bottom:2px solid var(--line);color:var(--brand);font-weight:800;padding:10px 12px;margin-top:10px;letter-spacing:.12em;font-size:10px}
.totals-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:6px}
.totals-card{border:1px solid var(--line);border-radius:8px;padding:14px}
.totals-card h4{margin:.2rem 0 .6rem;font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#444}
.rowline{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px dashed #eee;font-size:10px}
.rowline:last-child{border-bottom:0}
.subtot{font-weight:700;color:var(--brand)!important}
.subtot span{color:var(--brand)!important}
@media print{
  body{background:#fff}
  .page{width:auto;margin:0;padding:0}
  a{color:inherit;text-decoration:none}
  .row{display:flex;flex-wrap:wrap}
  .col-md-4{flex:0 0 33.333%;max-width:33.333%}
  .page-break{break-before:page;page-break-before:always}
  .no-break,table,tr,td{break-inside:avoid;page-break-inside:avoid}
}
</style>
</head>
`;

export const renderGeneratedPaymentReportLegacyHTML = (report) => {
    const data = parseJson(get(report, "json_data", {}));
    const basis = get(data, "payment_report_basis", {});
    const payment = get(data, "payment", {});
    const invoice = get(data, "invoice", {});
    const routingSheet = get(report, "routing_sheet", {}) || {};
    const rsData = parseJson(get(routingSheet, "json_data", {}));
    const ledgerRows = asArray(get(report, "gci_distribution_ledger", []));
    const distObj = buildLegacyDistObj({ basis, ledgerRows });

    const rsType = Number(get(routingSheet, "type", get(invoice, "rs_type", 0)));
    let lesseeTitle = "Lessee";
    let lessorTitle = "Lessor";
    if (rsType === 3) {
        lesseeTitle = "Sub-Lessee";
        lessorTitle = "Sub-Lessor";
    } else if (rsType === 4) {
        lesseeTitle = "Buyer";
        lessorTitle = "Seller";
    }

    const invoiceNumber = get(data, "invoice_number", get(invoice, "invoice_num", get(data, "invoice_id", "")));
    const depositDateRow = payment.deposit_date
        ? `<dt>Deposit Date</dt><dd>${escapeHtml(payment.deposit_date)}</dd>`
        : "";
    const receivedDateRow = payment.payment_received_date
        ? `<dt>Payment Received Date</dt><dd>${escapeHtml(payment.payment_received_date)}</dd>`
        : "";
    const presplitRows = distObj.presplits.length === 0
        ? `<tr><td><em>None</em></td><td class="center"></td><td class="right"></td><td class="center"></td></tr>`
        : distObj.presplits.map(item => `
          <tr>
            <td>${lineName(item)}</td>
            <td class="center">${percent(item.perc)}</td>
            <td class="right">${money(item.val)}</td>
            <td class="center">${escapeHtml(item.applies_to)}</td>
          </tr>
        `).join("");
    const lesseeTable = renderSideTable({
        title: lesseeTitle,
        outsideBrokers: distObj.lessee_outside_brokers,
        referrals: distObj.lessee_referrals,
        associates: distObj.lessee_illi_associates
    });
    const lessorTable = renderSideTable({
        title: lessorTitle,
        outsideBrokers: distObj.lessor_outside_brokers,
        referrals: distObj.lessor_referrals,
        associates: distObj.lessor_illi_associates
    });
    const outsideBrokerTotals = distObj.lessee_outside_brokers
        .concat(distObj.lessor_outside_brokers)
        .map(item => `<div class="rowline"><span>${lineName(item)}</span><span>${money(item.dollar_value)}</span></div>`)
        .join("");
    const referralTotals = distObj.presplits
        .map(item => `<div class="rowline"><span>${lineName(item)}</span><span>${money(item.val)}</span></div>`)
        .concat(distObj.lessee_referrals.concat(distObj.lessor_referrals).map(item => (
            `<div class="rowline"><span>${lineName(item)}</span><span>${money(item.dollar_value)}</span></div>`
        )))
        .join("");
    const associateTotals = Object.keys(distObj.all_assocs)
        .map(name => `<div class="rowline"><span>${escapeHtml(name)}</span><span>${money(distObj.all_assocs[name])}</span></div>`)
        .join("");
    const html = `
  <div class="page">
    <div class="header">
      <img class="logo" src="https://staging.connect.illicre.com/assets/logo.png" alt="illi commercial real estate logo">
      <div class="title">RS - Payment Report</div>
    </div>
    <section class="bootstrap-wrapper">
      <div class="container-fluid">
        <div class="row">
          <div class="col-6">
            <dl class="kvlist">
              <dt>Payment Date</dt><dd>${escapeHtml(formatDateOnly(payment.payment_date))}</dd>
              <dt>illi#</dt><dd>${escapeHtml(get(rsData, "illi_number", ""))}</dd>
              <dt>Project Name</dt><dd>${escapeHtml(get(routingSheet, "project_name", get(rsData, "project_name", "")))}</dd>
              <dt>Invoice #</dt><dd>${escapeHtml(invoiceNumber)}</dd>
              <dt>Check Number</dt><dd>${escapeHtml(payment.check_number || "")}</dd>
              <dt>Check Amount</dt><dd>${money(payment.payment_amount || get(basis, "payment_amount", 0))}</dd>
            </dl>
          </div>
          <div class="col-6">
            <dl class="kvlist">
              <dt>${lesseeTitle}</dt><dd>${escapeHtml(renderPartyNames(rsData, "lessees"))}</dd>
              <dt>${lessorTitle}</dt><dd>${escapeHtml(renderPartyNames(rsData, "lessors"))}</dd>
              <dt>Lease Signed Date</dt><dd>${escapeHtml(get(rsData, "date_lease_signed", ""))}</dd>
              ${depositDateRow}
              ${receivedDateRow}
            </dl>
          </div>
        </div>
      </div>
    </section>

    <div class="bar">PRE-SPLITS</div>
    <table aria-label="Pre-splits">
      <thead>
        <tr>
          <th>Referrals</th>
          <th class="center">Percentage</th>
          <th class="right">Dollar Value</th>
          <th class="center">Applies to</th>
        </tr>
      </thead>
      <tbody>
        ${presplitRows}
      </tbody>
    </table>

    <div class="bar bar--brand">POST-SPLITS</div>
    ${lesseeTable}
    ${lessorTable}

    <div class="totals-bar">TOTALS</div>
    <div class="totals-grid">
      <div class="totals-card">
        <h4>Outside Brokers</h4>
        ${outsideBrokerTotals}
        <div class="rowline subtot"><span>TOTAL:</span><span>${money(distObj.total_outside_brokers)}</span></div>

        <h4 style="margin-top:14px;">Referrals</h4>
        ${referralTotals}
        <div class="rowline subtot"><span>TOTAL:</span><span>${money(distObj.total_referrals)}</span></div>
      </div>

      <div class="totals-card">
        <h4>illi Associates</h4>
        ${associateTotals}
        <div class="rowline"><span>House</span><span>${money(distObj.total_house)}</span></div>
        <div class="rowline subtot"><span>TOTAL:</span><span>${money(distObj.total_illi_associates + distObj.total_house)}</span></div>
        <br>
        <br>
        <div class="rowline subtot">
          <span>TOTAL PAID:</span>
          <span>${money(distObj.total_paid)}</span>
        </div>
      </div>
    </div>
  </div>
  <div class="page-break"></div>
`;

    return `${headerHTML}<body>${html}</body></html>`;
};
